import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, X } from 'lucide-react';
import { fetchOrdersOnHold } from '../services/api';
import { getCurrentUser } from '../services/auth';
import { toTitleCase } from '../utils/format';

const RC_PROXY = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions/ringcentral-proxy'
  : '/.netlify/functions/ringcentral-proxy';

async function sendSms(to, text) {
  const phone = to.replace(/\D/g, '').replace(/^(\d{10})$/, '+1$1');
  const res = await fetch(RC_PROXY, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'sms-send', params: { to: phone, text } }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'SMS send failed');
  return data;
}

// Same day count as the admin portal: blank with no date, negative once it has passed.
const daysLeft = (ymd) => {
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((new Date(y, m - 1, d).getTime() - today.getTime()) / 86400000);
};
const showDate = (ymd) => {
  if (!ymd) return '—';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: '2-digit', day: '2-digit', year: 'numeric' });
};

export default function OnHold({ navigateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [smsRow, setSmsRow] = useState(null); // row being texted
  const [smsBody, setSmsBody] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsResult, setSmsResult] = useState(null);

  useEffect(() => {
    fetchOrdersOnHold()
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Most days left first, no date last, as the admin portal sorts it.
  const rows = [...(Array.isArray(data) ? data : data?.data ?? data?.records?.data ?? [])]
    .sort((a, b) => (daysLeft(b.expirationDate) ?? -Infinity) - (daysLeft(a.expirationDate) ?? -Infinity));
  const total = data?.totalRows ?? data?.total ?? rows.length;
  const totalRevenue = rows.reduce((s, r) => s + Number(r.orderTotal ?? 0), 0);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Orders on Hold</h2>
      </div>

      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 12 }}>
          {[
            { label: 'Orders on Hold', value: total.toLocaleString() },
            { label: 'Total Revenue', value: `$${totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
          ].map(({ label, value }) => (
            <div key={label} className="card" style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {/* SMS Compose Panel */}
      {smsRow && (
        <div className="card" style={{ marginBottom: 12, border: '1px solid var(--orange)', borderRadius: 8 }}>
          <div style={{ padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <MessageSquare size={13} /> Text {toTitleCase(smsRow.billTo)} — {smsRow.phone}
              </div>
              <button onClick={() => setSmsRow(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)' }}><X size={14} /></button>
            </div>
            <textarea
              value={smsBody}
              onChange={e => setSmsBody(e.target.value)}
              rows={3}
              className="form-input"
              style={{ width: '100%', fontSize: 12, resize: 'vertical', marginBottom: 8 }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                className="btn btn-primary"
                disabled={smsSending || !smsBody.trim()}
                style={{ fontSize: 11, padding: '6px 16px', display: 'flex', alignItems: 'center', gap: 4 }}
                onClick={async () => {
                  setSmsSending(true); setSmsResult(null);
                  try {
                    await sendSms(smsRow.phone, smsBody);
                    setSmsResult({ ok: true, msg: 'SMS sent!' });
                    setTimeout(() => setSmsRow(null), 2000);
                  } catch (err) { setSmsResult({ ok: false, msg: err.message }); }
                  finally { setSmsSending(false); }
                }}
              >
                <Send size={12} /> {smsSending ? 'Sending...' : 'Send SMS'}
              </button>
              {smsResult && <span style={{ fontSize: 11, fontWeight: 600, color: smsResult.ok ? '#1a8a4a' : '#c0392b' }}>{smsResult.msg}</span>}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">Orders on Hold {total > 0 && `(${total.toLocaleString()})`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No orders on hold.</div>
        )}
        {rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order ID</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Type</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Delivery Date</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Expires</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Days Left</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Total</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Text</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} onClick={() => navigateTo?.('order-details', row.bcOrderId ?? row.orderId)} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}>
                    <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{row.bcOrderId ?? row.orderId ?? '—'}</td>
                    <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>{toTitleCase(row.billTo ?? row.customerName ?? '—')}</td>
                    <td style={{ padding: '8px 8px', color: 'var(--gray-700)' }}>{row.shipType ?? '—'}</td>
                    <td style={{ padding: '8px 8px', color: 'var(--gray-700)' }}>{showDate(row.deliveryDate)}</td>
                    <td style={{ padding: '8px 8px', color: 'var(--gray-700)' }}>{showDate(row.expirationDate)}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'center', color: daysLeft(row.expirationDate) != null && daysLeft(row.expirationDate) <= 7 ? 'var(--red)' : 'var(--gray-700)' }}>{daysLeft(row.expirationDate) ?? ''}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600 }}>${Number(row.orderTotal ?? 0).toFixed(2)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                      {row.orderDate ? new Date(row.orderDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                      {row.phone ? (
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            const user = getCurrentUser();
                            const firstName = (row.firstName || row.billTo?.split(' ')[0] || '');
                            setSmsBody(`Hi ${toTitleCase(firstName)}, it's ${user?.staffName || 'The Crab Place'} with CrabPlace.com and I'm contacting you about your order.`);
                            setSmsRow(row);
                            setSmsResult(null);
                          }}
                          style={{ background: 'none', border: '1px solid var(--gray-200)', borderRadius: 4, cursor: 'pointer', padding: '3px 8px', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--navy)', fontWeight: 600 }}
                        >
                          <MessageSquare size={11} /> Text
                        </button>
                      ) : <span style={{ fontSize: 10, color: 'var(--gray-300)' }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
